import type {ReactElement} from 'react';
import React, {useEffect} from 'react';
import {EdgeType} from '@PosterWhiteboard/classes/item-effects.class';
import {ThumbnailItemSelectionState} from '@Components/thumbnail-item';
import {Thumbnail} from '@Components/thumbnail';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import {EdgeEffectOptionsDataArray} from '@Panels/poster-editor-item-effects-panel/components/base-edge-effects-options/base-edge-effects-options.types';
import {getAssetUrl} from '@Utils/s3.util';
import {BaseGradientEdgeEffectThicknessOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-gradient-edge-effect-thickness-option';
import {updateOptionsPanelHeight} from '@Components/poster-editor/library/poster-editor-library';
import {ContextualMenu} from '@Components/contextual-menu';
import {ContextualMenuThumbnailItem} from '@Components/contextual-menu/components/contextual-menu-thumbnail-item';
import {getCompatibleVideoFileExtension} from '@Utils/video.util';
import {ContextualMenuHorizontalScrollingSection} from '@Components/contextual-menu/components/contextual-menu-horizontal-scrolling-section';
import styles from './base-edge-effects-options.module.scss';

interface BaseEdgeEffectsOptionsProps {
  panelId: string;
  selectedEdgeType: EdgeType;
  edgeThickness: number;

  onClickEdgeType(type: EdgeType): void;

  onUpdateThickness(value: number, undoable?: boolean): void;
}

const THUMBNAIL_BASE_URL = 'poster-editor-thumbnails/edge_effects/';

export function BaseEdgeEffectsOptions({...props}: BaseEdgeEffectsOptionsProps): ReactElement | null {
  useEffect((): void => {
    updateHeightInRedux(props.selectedEdgeType);
  }, [props.selectedEdgeType]);

  const updateHeightInRedux = (effect: EdgeType): void => {
    let height = '182px';
    if (effect === EdgeType.GRADIENT) {
      height = '260px';
    }

    updateOptionsPanelHeight(props.panelId, height);
  };

  const getTextForOption = (effect: EdgeType): string => {
    switch (effect) {
      case EdgeType.NONE:
        return window.i18next.t('pmwjs_none');
      case EdgeType.CIRCULAR:
        return window.i18next.t('pmwjs_circular');
      case EdgeType.GRADIENT:
        return window.i18next.t('pmwjs_background_gradient');
      case EdgeType.HEART:
        return window.i18next.t('pmwjs_heart');
      case EdgeType.OVAL:
        return window.i18next.t('pmwjs_oval');
      case EdgeType.RECTANGLE:
        return window.i18next.t('pmwjs_rectangle');
      case EdgeType.SCRATCHED:
        return window.i18next.t('pmwjs_scratched');
      case EdgeType.TORN_PAPER:
        return window.i18next.t('pmwjs_torn_paper');
      default:
        return window.i18next.t('pmwjs_none');
    }
  };

  const getOptions = (): ReactElement[] => {
    return EdgeEffectOptionsDataArray.map((effect) => {
      return (
        <ContextualMenuThumbnailItem
          key={effect.effect}
          className={styles.thumbnailsClasses}
          thumbnailContainerClassName={styles.largeThumbnail}
          thumbnail={
            <Thumbnail
              className={styles.thumbnail}
              data={{
                type: ThumbnailType.IMAGE,
                imageSrc: getAssetUrl(`${THUMBNAIL_BASE_URL}${effect.thumbnail}.${getCompatibleVideoFileExtension('webp')}`),
              }}
            />
          }
          isSelected={props.selectedEdgeType === effect.effect}
          selectionState={ThumbnailItemSelectionState.OUTLINE}
          text={getTextForOption(effect.effect)}
          textClassName="content-body spacing-m-t-1"
          hasBorder={false}
          onClick={(): void => {
            if (effect.effect === props.selectedEdgeType) {
              return;
            }
            props.onClickEdgeType(effect.effect);
          }}
        />
      );
    });
  };

  const onUpdateThickness = (val: number, undoable = true): void => {
    props.onUpdateThickness(val, undoable);
  };

  const getSubOptions = (): ReactElement | null => {
    if (props.selectedEdgeType === EdgeType.GRADIENT) {
      return <BaseGradientEdgeEffectThicknessOption edgeThickness={props.edgeThickness} onUpdateThickness={onUpdateThickness} />;
    }
    return null;
  };

  return (
    <ContextualMenu id="edge-effects-mobile-options" includeLeftRightPadding={false} contentClassName="spacing-m-0 spacing-p-t-1 spacing-p-b-4" hasMobileMorphingProperties>
      {props.selectedEdgeType === EdgeType.GRADIENT ? <div className="spacing-p-r-4 spacing-p-l-4 spacing-m-b-4">{getSubOptions()}</div> : null}
      <ContextualMenuHorizontalScrollingSection className={`flex-h-row spacing-m-0 spacing-p-l-4 spacing-p-r-4 ${styles.list}`}>
        {getOptions()}
      </ContextualMenuHorizontalScrollingSection>
    </ContextualMenu>
  );
}
